import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangleIcon, RefreshCwIcon } from "lucide-react";
import { Button } from "@component/button";
import { Card, CardContent } from "@component/card";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

export type AsyncState<T> = {
  data: T | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
};

export function useTrainingAsync<T>(
  fn: () => Promise<T>,
  deps: React.DependencyList = [],
  options: { logLabel?: string } = {}
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const cancelled = useRef(false);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    cancelled.current = false;
    setLoading(true);
    setError(null);
    fn()
      .then((result) => {
        if (cancelled.current) return;
        setData(result);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled.current) return;
        const e = err instanceof Error ? err : new Error(String(err));
        const label = options.logLabel || "training_async";
        catchErrorLog(`[training] ${label} failed: ${e.message}`);
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, loading, error, reload };
}

export function TrainingErrorCard({
  title = "We couldn't load this section.",
  message,
  onRetry,
  className,
}: {
  title?: string;
  message?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardContent className="p-8 text-center">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-50 text-amber-600 mb-3">
          <AlertTriangleIcon className="w-5 h-5" />
        </div>
        <div className="text-sm font-medium text-gray-900 mb-1">{title}</div>
        <div className="text-xs text-gray-500 mb-4 max-w-sm mx-auto">
          {message ||
            "Something went wrong on our end. The error has been reported. You can try again, or refresh in a minute."}
        </div>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCwIcon className="w-3.5 h-3.5 mr-1.5" />
            Try again
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export function TrainingEmptyState({
  icon: Icon,
  title,
  message,
  action,
  className,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  message?: string;
  action?: { label: string; onClick: () => void };
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardContent className="p-12 text-center">
        {Icon && (
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 text-gray-400 mb-4">
            <Icon className="w-6 h-6" />
          </div>
        )}
        <div className="text-base font-medium text-gray-900 mb-2">{title}</div>
        {message && (
          <div className="text-sm text-gray-500 mb-6 max-w-md mx-auto">{message}</div>
        )}
        {action && <Button onClick={action.onClick}>{action.label}</Button>}
      </CardContent>
    </Card>
  );
}

export function TrainingSkeletonRows({
  rows = 4,
  className,
}: {
  rows?: number;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2.5", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-100 animate-pulse flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="h-3 w-32 bg-gray-100 rounded animate-pulse mb-1.5" />
            <div className="h-2 w-20 bg-gray-100 rounded animate-pulse" />
          </div>
          <div className="h-3 w-12 bg-gray-100 rounded animate-pulse flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}

export function TrainingSkeletonStatTiles({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="p-4">
            <div className="h-3 w-24 bg-gray-100 rounded animate-pulse mb-2" />
            <div className="h-7 w-16 bg-gray-100 rounded animate-pulse" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
